/*
 * sp-rail.js
 * Vanilla JS for sp-* sections: horizontal scroll-snap rails (progress bar,
 * prev/next buttons, arrow keys) and an ARIA tabs controller.
 *
 * Hooks (data attributes):
 *   [data-sp-rail]                 rail wrapper ([data-sp-rail-auto]: opt-in slow auto-scroll)
 *     [data-sp-rail-track]         scroll container (focusable, arrow keys)
 *     [data-sp-rail-bar]           progress fill (scaleX 0..1)
 *     [data-sp-rail-prev|next]     step buttons
 *     [data-sp-rail-controls]      row hidden when nothing overflows
 *   [data-sp-tabs-root]            tabs wrapper
 *     [role=tab][data-sp-tab]      tab buttons (aria-controls -> panel id)
 *     [data-sp-tab-panel]          panels (toggled with the hidden attribute)
 * Event: dispatch "sp-rail:update" on a [data-sp-rail] to recompute its state.
 * Public: window.SPRail.init(scope) (idempotent).
 */
(function () {
  'use strict';

  if (window.SPRail) return;

  var READY = 'spRailReady';
  var TABS_READY = 'spTabsReady';

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function scrollBehavior() {
    return prefersReducedMotion() ? 'auto' : 'smooth';
  }

  function initRail(root) {
    if (!root || root.dataset[READY]) return;
    var track = root.querySelector('[data-sp-rail-track]');
    if (!track) return;
    root.dataset[READY] = 'true';

    var bar = root.querySelector('[data-sp-rail-bar]');
    var prev = root.querySelector('[data-sp-rail-prev]');
    var next = root.querySelector('[data-sp-rail-next]');
    var controls = root.querySelector('[data-sp-rail-controls]');
    var ticking = false;

    function update() {
      ticking = false;
      var width = track.clientWidth;
      if (!width) return; // hidden panel; refreshed when shown
      var total = track.scrollWidth;
      var max = total - width;
      var ratio = total > 0 ? Math.min(1, (track.scrollLeft + width) / total) : 1;
      if (bar) bar.style.transform = 'scaleX(' + ratio + ')';
      if (prev) prev.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft >= max - 1;
      if (controls) controls.hidden = max <= 1;
    }

    function requestUpdate() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    function step() {
      var item = track.querySelector('li');
      if (!item) return track.clientWidth;
      var gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
      var unit = item.offsetWidth + gap;
      if (!unit) return track.clientWidth;
      return unit * Math.max(1, Math.floor(track.clientWidth / unit));
    }

    function go(direction) {
      track.scrollBy({ left: direction * step(), behavior: scrollBehavior() });
    }

    track.addEventListener('scroll', requestUpdate, { passive: true });
    root.addEventListener('sp-rail:update', update);
    window.addEventListener('resize', requestUpdate);

    if (prev) prev.addEventListener('click', function () { go(-1); });
    if (next) next.addEventListener('click', function () { go(1); });

    track.addEventListener('keydown', function (event) {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.target !== track) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        go(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        go(-1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        track.scrollTo({ left: 0, behavior: scrollBehavior() });
      } else if (event.key === 'End') {
        event.preventDefault();
        track.scrollTo({ left: track.scrollWidth, behavior: scrollBehavior() });
      }
    });

    if (typeof window.ResizeObserver === 'function') {
      new window.ResizeObserver(requestUpdate).observe(track);
    }

    initAutoScroll(root, track);

    update();
  }

  // Opt-in auto-scroll ([data-sp-rail-auto]): drifts right-to-left at a slow constant speed, pauses at the end,
  // then returns to the start. Stops on interaction, off-screen, hidden tab and reduced-motion.
  function initAutoScroll(root, track) {
    if (!root.hasAttribute('data-sp-rail-auto')) return;
    if (prefersReducedMotion()) return;

    var SPEED = 30; // px per second
    var END_PAUSE = 1800; // ms to wait at the end before returning
    var RESUME_DELAY = 3000; // ms after the user lets go
    var raf = 0;
    var last = 0;
    var pos = 0;
    var visible = false;
    var holdUntil = 0; // Infinity while the user is touching / hovering / focused
    var endAt = 0;
    var canHover = !!(window.matchMedia && window.matchMedia('(hover: hover)').matches);

    function now() { return window.performance && performance.now ? performance.now() : Date.now(); }

    function shouldRun() {
      return visible && !document.hidden;
    }

    function tick(time) {
      raf = 0;
      if (!shouldRun()) { last = 0; return; }
      raf = window.requestAnimationFrame(tick);
      var dt = last ? Math.min(100, time - last) : 0;
      last = time;
      var width = track.clientWidth;
      if (!width) return; // hidden panel
      if (time < holdUntil) { pos = track.scrollLeft; return; }
      var max = track.scrollWidth - width;
      if (max <= 1) return;
      if (endAt) {
        if (time < endAt) return;
        endAt = 0;
        pos = 0;
        track.scrollLeft = 0;
        holdUntil = time + 800;
        return;
      }
      // Re-sync after any outside scroll (prev/next buttons, keyboard) so we never snap back
      if (Math.abs(pos - track.scrollLeft) > 2) pos = track.scrollLeft;
      pos = Math.min(max, pos + (SPEED * dt) / 1000);
      track.scrollLeft = pos;
      if (pos >= max - 0.5) endAt = time + END_PAUSE;
    }

    function sync() {
      if (shouldRun()) {
        if (!raf) { last = 0; pos = track.scrollLeft; raf = window.requestAnimationFrame(tick); }
      } else if (raf) {
        window.cancelAnimationFrame(raf);
        raf = 0;
        last = 0;
      }
    }

    function hold() { holdUntil = Infinity; endAt = 0; }
    function release() { holdUntil = now() + RESUME_DELAY; }

    ['pointerdown', 'touchstart'].forEach(function (name) {
      track.addEventListener(name, hold, { passive: true });
    });
    ['pointerup', 'pointercancel', 'touchend', 'touchcancel'].forEach(function (name) {
      track.addEventListener(name, release, { passive: true });
    });
    track.addEventListener('wheel', release, { passive: true });
    root.addEventListener('focusin', function (event) {
      var focusVisible = true;
      try { focusVisible = event.target.matches(':focus-visible'); } catch (e) { focusVisible = true; }
      if (focusVisible) hold();
    });
    root.addEventListener('focusout', release);
    if (canHover) {
      root.addEventListener('mouseenter', hold);
      root.addEventListener('mouseleave', release);
    }

    document.addEventListener('visibilitychange', sync);

    if (typeof window.IntersectionObserver === 'function') {
      new window.IntersectionObserver(function (entries) {
        visible = entries[entries.length - 1].isIntersecting;
        sync();
      }).observe(root);
    } else {
      visible = true;
      sync();
    }
  }

  function initTabs(root) {
    if (!root || root.dataset[TABS_READY]) return;
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[role="tab"][data-sp-tab]'));
    if (!tabs.length) return;
    root.dataset[TABS_READY] = 'true';

    // Look the panel up inside this tabs root: ids are section-scoped now, but the home page holds several
    // tab groups, so never resolve through the whole document.
    function panelOf(tab) {
      var id = tab.getAttribute('aria-controls');
      if (!id) return null;
      var panels = root.querySelectorAll('[data-sp-tab-panel]');
      for (var i = 0; i < panels.length; i += 1) {
        if (panels[i].id === id) return panels[i];
      }
      return null;
    }

    function select(tab, focus) {
      tabs.forEach(function (item) {
        var active = item === tab;
        item.setAttribute('aria-selected', active ? 'true' : 'false');
        item.tabIndex = active ? 0 : -1;
        var panel = panelOf(item);
        if (panel) panel.hidden = !active;
      });
      if (focus) tab.focus();
      var shown = panelOf(tab);
      if (shown) {
        // Images of a panel that was hidden at load are still lazy: load them as soon as the tab opens.
        Array.prototype.forEach.call(shown.querySelectorAll('img[loading="lazy"]'), function (img) {
          img.loading = 'eager';
        });
        Array.prototype.forEach.call(shown.querySelectorAll('[data-sp-rail]'), function (rail) {
          rail.dispatchEvent(new Event('sp-rail:update'));
        });
      }
    }

    tabs.forEach(function (tab, index) {
      tab.addEventListener('click', function () { select(tab, false); });
      tab.addEventListener('keydown', function (event) {
        var target = null;
        if (event.key === 'ArrowRight') target = tabs[(index + 1) % tabs.length];
        else if (event.key === 'ArrowLeft') target = tabs[(index - 1 + tabs.length) % tabs.length];
        else if (event.key === 'Home') target = tabs[0];
        else if (event.key === 'End') target = tabs[tabs.length - 1];
        if (!target) return;
        event.preventDefault();
        select(target, true);
      });
    });
  }

  function init(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var rails = Array.prototype.slice.call(context.querySelectorAll('[data-sp-rail]'));
    if (context.matches && context.matches('[data-sp-rail]')) rails.push(context);
    rails.forEach(initRail);

    var tabRoots = Array.prototype.slice.call(context.querySelectorAll('[data-sp-tabs-root]'));
    if (context.matches && context.matches('[data-sp-tabs-root]')) tabRoots.push(context);
    tabRoots.forEach(initTabs);
  }

  window.SPRail = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Theme editor: re-init a re-rendered section, and follow block selection.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  document.addEventListener('shopify:block:select', function (event) {
    var target = event.target;
    if (target && target.matches && target.matches('[role="tab"][data-sp-tab]')) target.click();
  });
})();
